// Bounds for the declared weekly-hours field. They live here because the input
// and the validation schema both need them and neither should import the other:
// as separate literals they can drift apart without anything noticing.
// The field is only offered on full time, and 50 is its ceiling (Andrea's review,
// Edoardo on 28/09/2026): the same line at which AdValidator blocks an ad (it
// warns between 40 and 50). The changeset stays the wider outer guard and drops
// anything outside 1..90. This form must never be wider than the backend.
export const WEEKLY_HOURS_MIN = 1;
export const WEEKLY_HOURS_MAX = 50;

import type { JobPosition, Variable, Skill, Occupation } from '@restworld/utility-types';

export type AccommodationChoice = 'single' | 'shared' | 'none';

// JPOnboardingType is a *flat* projection of JobPosition restricted to the
// fields actually touched by the onboarding form. A full JobPosition has ~170
// fields and react-hook-form's FieldPath<T> / PathValue<T> expand recursively,
// which causes tsc to run into OOM during type inference. Exposing only the
// fields used by the form keeps the recursion bounded.
// Runtime values are still plain JobPosition objects returned by the API —
// this type only narrows what useFormContext<JPOnboardingType> sees.

type OnboardingRestaurant = {
  id?: string;
  restaurant_name?: string | null;
  address?: string | null;
  country?: string | null;
  brief_description?: string | null;
};

export interface JPOnboardingType {
  // Identity & restaurant
  id?: string;
  restaurant_id?: string;
  restaurant_name?: string;
  restaurant?: OnboardingRestaurant;
  status_key?: string;

  // Role / occupation
  occupation?: Occupation;
  occupation_id?: number;
  job_title?: string;
  job_type?: string;
  job_type_var?: Variable;
  job_description?: string;
  job_contract_description?: string;

  // Domain / experience
  domain_interest?: string;
  domain_interest_var?: Variable;
  experience?: string;
  experience_var?: Variable;
  experience_specifics?: string;
  experience_specifics_var?: Variable;

  // Contract
  contract_type?: string;
  contract_type_var?: Variable;

  // Shifts
  shift_category?: string;
  shift_category_var?: Variable;
  shift_notes?: string;
  days_required_per_week_number?: number;

  // Salary
  payment_type?: string;
  payment_type_var?: Variable;
  salary?: number | null;
  min_salary?: number | null;
  max_salary?: number | null;
  salary_currency?: string;
  salary_type?: 'net' | 'gross';
  salary_month_number?: number | null;

  // Pay-transparency (D.Lgs. 96/2026) estimated salary values for Italian venues.
  // Computed on the frontend from the single figure the employer enters and persisted
  // verbatim. Each value is exact + min/max range (mirrors salary/min_salary/max_salary).
  salary_ral?: number | null;
  salary_ral_min?: number | null;
  salary_ral_max?: number | null;
  salary_monthly_gross?: number | null;
  salary_monthly_gross_min?: number | null;
  salary_monthly_gross_max?: number | null;
  salary_monthly_net?: number | null;
  salary_monthly_net_min?: number | null;
  salary_monthly_net_max?: number | null;
  salary_hourly_gross?: number | null;
  salary_hourly_gross_min?: number | null;
  salary_hourly_gross_max?: number | null;
  salary_hourly_net?: number | null;
  salary_hourly_net_min?: number | null;
  salary_hourly_net_max?: number | null;
  // Frontend-only (stripped before the API call): the weekly hours used to estimate
  // on-call salaries, and the employer's confirmation of the estimated values.
  on_call_weekly_hours?: number | null;
  salary_estimates_confirmed?: boolean;

  // Skills / certificates / languages
  required_skills?: Skill[];
  additional_skills?: string[];
  certificates?: { certificate_var: Variable }[];
  certificate_strict?: boolean;
  languages?: { language: string; language_var?: Variable; level: string; level_var?: Variable }[];

  // Benefits & other requirements
  benefits?: { benefit_var: Variable }[];
  additional_benefits?: string[];
  other_requirements?: { other_requirement_var: Variable }[];

  // Seasonal / accommodation
  'seasonal?'?: boolean;
  season_starts_at?: string | null;
  season_ends_at?: string | null;
  desired_start_type?: 'asap' | 'exact_date' | 'indicative_date' | null;
  desired_start_date?: string | null;
  accommodation_choice?: AccommodationChoice;
  accommodation_details?: string | null;

  // Conditional free-text fields
  kitchen_station?: string | null;
  part_time_weekly_hours?: string | null;
  weekly_hours?: number | null;

  // Misc
  staff_requested?: number;
  advertised_location?: string;
  worker_max_distance?: number;
  show_restaurant_details?: boolean;
  additional_description_information?: string;
}

// Convenience: used when the form is initialised from an API payload.
// JobPosition is broader than JPOnboardingType, so this cast is explicit.
export const asOnboardingType = (jp: JobPosition): JPOnboardingType => jp as unknown as JPOnboardingType;

// Fields that exist only in the form state and must NOT reach the backend.
// `salary_estimates_confirmed` drives the Italian pay-transparency confirm UX but is not
// persisted. `on_call_weekly_hours` IS persisted now (read-only column) so the on-call salary step
// can restore it on reload instead of re-asking; the 15 salary_* estimates persist too.
const FRONTEND_ONLY_FIELDS = ['accommodation_choice', 'salary_estimates_confirmed'] as const;

// Strip frontend-only keys before sending to the API.
export const toJobPositionPayload = (data: Partial<JPOnboardingType>): Partial<JobPosition> => {
  const sanitized: Record<string, unknown> = { ...data };
  for (const key of FRONTEND_ONLY_FIELDS) {
    delete sanitized[key];
  }
  return sanitized as Partial<JobPosition>;
};
